/**
 * #/flush-all — clears THIS browser and nothing else.
 *
 * No server call, no session, no permission check: it is your browser, so it is
 * always allowed and can never be refused. Safe to hand to anyone over the phone
 * when their page is showing something stale.
 *
 * It runs on arrival rather than waiting for a click, because the URL IS the
 * instruction. The server-wide equivalent is a separate screen with a separate
 * button — see FlushServer.
 */
import { useEffect, useState } from 'react';
import { useFlushLocal } from '../data/flush';
import { SCREENS } from './routes';

export function FlushLocal({ onClose }: { onClose: () => void }) {
  const flush = useFlushLocal();
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void flush().then(() => { if (!cancelled) setDone(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flags-page">
      <div className="container flags-inner">
        <header className="flags-head">
          <div>
            <h1>Flush cache</h1>
            <p>
              {done
                ? 'Done. Every section will be re-fetched from the server.'
                : 'Dropping cached sections…'}
            </p>
          </div>
          <button onClick={onClose}>← Back to site</button>
        </header>

        <ul className="flush-list">
          <li className={done ? 'done' : ''}>
            <strong>This browser</strong>
            <span>Cached sections, version numbers and permissions removed from memory and localStorage.</span>
          </li>
          <li className="skipped">
            <strong>Everyone else</strong>
            <span>
              Untouched. This page never contacts the server — flushing for every
              visitor is <a href={SCREENS.flushServer}>#/flush-server</a>, and it is
              admin only.
            </span>
          </li>
        </ul>

        <div className="flags-foot">
          <button onClick={() => { window.location.hash = SCREENS.home; }}>Back to the site</button>
          <p>
            Rarely needed: the cache is version-aware, so a stale client sends its
            version and the server replies <code>CURRENT</code> or sends the new content.
          </p>
        </div>
      </div>
    </div>
  );
}
